import isConfirmPricingStepEnabledSelector from '../selectors/isConfirmPricingStepEnabledSelector'
import orderSelector from '../selectors/orderSelector'
import { getNewOrderUrl } from "../../../../shared/utils/routes"
import * as OrderUtils from "../../../../shared/utils/OrderUtils"

export const STEP_CALENDAR = 1
export const STEP_PRODUCT = 2
export const STEP_CONFIRM_PRICING = 3
export const STEP_CUSTOMER_DETAILS = 4
export const STEP_PAYMENT = 5

function isErrorsObjectEmpty(errors) {
  return Object.values(errors).filter(arr => arr.length > 0).length === 0;
}

export const CHANGE_CURRENT_STEP_START = "CHANGE_CURRENT_STEP_START";
export const CHANGE_CURRENT_STEP_FINISH = "CHANGE_CURRENT_STEP_FINISH";
export const CHANGE_CURRENT_STEP_CANCEL = "CHANGE_CURRENT_STEP_CANCEL";
export function changeCurrentStep(step) {
  return (dispatch, getState) => {
    const state = getState();

    dispatch({ type: CHANGE_CURRENT_STEP_START, step })

    if (step === STEP_CONFIRM_PRICING && !isConfirmPricingStepEnabledSelector(state)) {
      step = STEP_CUSTOMER_DETAILS;
    }

    const onSuccess = () => dispatch({ type: CHANGE_CURRENT_STEP_FINISH, step })
    const onFailure = error => dispatch({ type: CHANGE_CURRENT_STEP_CANCEL, step, error })

    // If dataLayer is initialized send events to Google Tag Manager dataLayer on step change.
    if (typeof dataLayer !== 'undefined') {
      const order = orderSelector(state);

      // If we're going from step 1 to step 2, send addToCart and checkout (step 1) events.
      // If we're going from earlier (2 or 3) step to step 4, send checkout (step 2) event.
      // If we're going back to step 1, send removeFromCart event.
      if (state.currentStep === STEP_CALENDAR && step === STEP_PRODUCT) {
        dataLayerAddToCart(order);
        dataLayerCheckout(order, 1);
      } else if (state.currentStep < STEP_CUSTOMER_DETAILS && step === STEP_CUSTOMER_DETAILS) {
        dataLayerCheckout(order, 2);
      } else if (step === STEP_CALENDAR) {
        dataLayerRemoveFromCart(order);
      }
    }

    // If we're going back to step 1/2 from step 3/4/5,
    // cancel the order first and refresh the product's availability data.
    dispatch(clearValidationErrors())
    if (state.currentStep > STEP_PRODUCT && step <= STEP_PRODUCT && state.orderData.id) {
      dispatch(abandonOrder())
        .then(
          () => {
            dispatch(updateHistoryState())
            dispatch(loadProductAndCalculateStep()).then(onSuccess)
          },
          onFailure
        );
    } else {
      onSuccess()
    }
    scrollToTop()
  }
}

function calculateCurrentStep() {
  return (dispatch, getState) => {
    const order = orderSelector(getState());
    const currentStep = getState().currentStep;
    const step = getState().loadingStep;

    return new Promise((resolve, reject) => {
      if (!currentStep && order.data.product_schedule_slot_id) {
        dispatch(loadProductScheduleSlotData(order.data.product_schedule_slot_id))
          .then(
            () => dispatch(submitCalendarForm())
              .then(
                () => resolve(STEP_PRODUCT),
                () => {
                  dispatch(clearValidationErrors())
                  resolve(STEP_CALENDAR)
                }
              )
          )
      } else if (currentStep == STEP_CALENDAR && !step && order.data.product_schedule_slot) {
        resolve(STEP_CALENDAR)
      } else  if (!order.still_building) {
        resolve(STEP_PAYMENT);
      } else if (order.data.id) {
        if (isConfirmPricingStepEnabledSelector(getState())) {
          resolve(STEP_CONFIRM_PRICING);
        } else {
          resolve(STEP_CUSTOMER_DETAILS);
        }
      } else if (order.data.booking_type === "static_date_booking" && order.product_variant && order.product_schedule_slot) {
        dispatch(submitCalendarForm())
          .then(
            () => resolve(STEP_PRODUCT),
            () => {
              dispatch(clearValidationErrors())
              resolve(STEP_CALENDAR)
            }
          )
      } else {
        if (order.data.booking_type === "no_booking") {
          const pv = order.product_variant || order.product.product_variants[0]
          dispatch(updateOrderData({
            product_variant_id: pv.id,
          }))
          dispatch(calculateOrderPricing())
            .then(
              () => resolve(STEP_PRODUCT),
              () => {
                dispatch(clearValidationErrors())
                console.error("error while calculating pricing for no_booking error, step will be null!")
                resolve(null)
              }
            )
        } else if (order.data.booking_type === "static_date_booking") {
          resolve(STEP_CALENDAR);
        }
      }
    })
  }
}

export const FETCH_ORDER_DATA_START = "FETCH_ORDER_DATA_START";
export const FETCH_ORDER_DATA_SUCCESS = "FETCH_ORDER_DATA_SUCCESS";
export const FETCH_ORDER_DATA_FAILURE = "FETCH_ORDER_DATA_FAILURE";
export function fetchOrderData() {
  return (dispatch, getState) => {
    const orderData = getState().orderData;
    if (!orderData.id) {
      throw new Error("fetchOrderData action impossible - orderData.id is empty")
    }

    dispatch({ type: FETCH_ORDER_DATA_START })

    return st.resources.orders.getOrder(orderData.id, orderData.token)
      .then(
        data => {
          dispatch({ type: UPDATE_ORDER_DATA, data: data.data.order })
          dispatch({ type: FETCH_ORDER_DATA_SUCCESS, data })

          return dispatch(calculateCurrentStep())
            .then(step => {
              if (step < STEP_PAYMENT) {
                dispatch(loadProductAndCalculateStep())
              } else {
                dispatch({ type: CHANGE_CURRENT_STEP_FINISH, step })
              }
            })
        },
        error => dispatch({ type: FETCH_ORDER_DATA_FAILURE, error })
      );
  }
}

export const INIT_HISTORY_STATE = "INIT_HISTORY_STATE";
export function initHistoryState() {
  return (dispatch, getState) => {
    const historyState = {
      options: {locationReload: true},
      title: window.document.title
    }

    dispatch({ type: INIT_HISTORY_STATE, historyState })
    // window.history.replaceState && window.history.replaceState(historyState.options, historyState.title, historyState.url)
  }
}

const originalWindowTitle = window.document.title
const originalWindowUrl = window.location.href

export const UPDATE_HISTORY_STATE = "UPDATE_HISTORY_STATE";
export function updateHistoryState() {
  return (dispatch, getState) => {
    const order = orderSelector(getState())

    const historyState = {
      options: { locationReload: true },
      title: order.data.id ? I18n.t("fe.NewOrderView.title", { order_id: order.data.id, site_name: order.data.app_config.site_name }) : originalWindowTitle,
      url: order.data.id ? order.data.url : originalWindowUrl,
    }

    dispatch({ type: UPDATE_HISTORY_STATE, historyState })
    // window.history.replaceState && window.history.replaceState(historyState.options, historyState.title, historyState.url)
    document.title = historyState.title
  }
}

export const UPDATE_ORDER_DATA = "UPDATE_ORDER_DATA";
export function updateOrderData(data) {
  return dispatch => {
    dispatch({ type: UPDATE_ORDER_DATA, data })
  }
}

export const LOAD_PRODUCT_DATA_START = "LOAD_PRODUCT_DATA_START";
export const LOAD_PRODUCT_DATA_SUCCESS = "LOAD_PRODUCT_DATA_SUCCESS";
export const LOAD_PRODUCT_DATA_FAILURE = "LOAD_PRODUCT_DATA_FAILURE";
export function loadProductData() {
  return (dispatch, getState) => {
    dispatch({ type: LOAD_PRODUCT_DATA_START })
    return st.resources.products.getProductFullData(getState().orderData.product_id)
      .then(
        data => {
          dispatch({ type: UPDATE_PRODUCT_DATA, data: data.product })
          dispatch({ type: LOAD_PRODUCT_DATA_SUCCESS, data })
        },
        error => dispatch({ type: LOAD_PRODUCT_DATA_FAILURE, error })
      );
  }
}

export const LOAD_PRODUCT_SCHEDULE_SLOT_DATA = "LOAD_PRODUCT_SCHEDULE_SLOT_DATA";
export function loadProductScheduleSlotData(id) {
  return (dispatch, getState) => {
    return st.resources.product_schedule_slots.getProductScheduleSlotData(getState().orderData.product_id, id)
      .then(
        data => {
          dispatch({ type: LOAD_PRODUCT_SCHEDULE_SLOT_DATA, data: { slot: data.product_schedule_slot } })
        }
      )
  }
}

export const LOAD_PRODUCT_SCHEDULE_SLOTS_DATA_START = "LOAD_PRODUCT_SCHEDULE_SLOTS_DATA_START";
export const LOAD_PRODUCT_SCHEDULE_SLOTS_DATA_SUCCESS = "LOAD_PRODUCT_SCHEDULE_SLOTS_DATA_SUCCESS";
export const LOAD_PRODUCT_SCHEDULE_SLOTS_DATA_FAILURE = "LOAD_PRODUCT_SCHEDULE_SLOTS_DATA_FAILURE";
export const UPDATE_PRODUCT_SCHEDULE_SLOT = "UPDATE_PRODUCT_SCHEDULE_SLOT";
export function loadProductScheduleSlotsData(data) {
  const { startDate, endDate, includeId } = data;

  return (dispatch, getState) => {
    dispatch({ type: LOAD_PRODUCT_SCHEDULE_SLOTS_DATA_START })
    return st.resources.product_schedule_slots.getProductScheduleSlotsData(getState().orderData.product_id, startDate, endDate, includeId)
      .then(
        data => {
          dispatch({ type: UPDATE_PRODUCT_SCHEDULE_SLOTS_DATA, data: { slots: data.product_schedule_slots, lastSlotSelectDate: endDate } })
          dispatch({ type: LOAD_PRODUCT_SCHEDULE_SLOTS_DATA_SUCCESS, data })
          dispatch({ type: UPDATE_PRODUCT_SCHEDULE_SLOT })
        },
        error => dispatch({ type: LOAD_PRODUCT_SCHEDULE_SLOTS_DATA_FAILURE, error })
      )
  }
}

export function loadProductScheduleSlotsDataAndCalculateStep(data) {
  return (dispatch) => {
    dispatch(loadProductScheduleSlotsData(data))
      .then(() => (
        dispatch(calculateCurrentStep())
          .then(step => (
            dispatch({ type: CHANGE_CURRENT_STEP_FINISH, step })
          ))
          .then(resolve, reject)
      ))
  }
}

export function loadProductAndCalculateStep() {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatch(loadProductData())
        .then(() => (
          dispatch(calculateCurrentStep())
            .then(step => (
              dispatch({ type: CHANGE_CURRENT_STEP_FINISH, step })
            ))
            .then(resolve, reject)
        ))
    })
  }
}

export const INITIALIZE_MAIN_VIEW_FINISH = "INITIALIZE_MAIN_VIEW_FINISH"
export function initializeMainView() {
  return (dispatch, getState) => {
    const order = orderSelector(getState())
    if (order.data.product_id) {
      return dispatch(loadProductAndCalculateStep())
        .then(() => {
          const state = getState()
          const order = orderSelector(state)
          const isValid = isErrorsObjectEmpty(state.errors)
          if (state.currentStep === STEP_PRODUCT && isValid && order.product.product_type === "coupon") {
            return dispatch(submitProductForm())
              .then(
              dispatch(changeCurrentStep(STEP_CUSTOMER_DETAILS))
              )
              .then(
              () => dispatch({ type: INITIALIZE_MAIN_VIEW_FINISH }),
              () => dispatch({ type: INITIALIZE_MAIN_VIEW_FINISH }),
            )
          // If dataLayer is initialized and we begin on step 2,
          // send addToCart and checkout (step 1) events to Google Tag Manager dataLayer.
          } else if (typeof dataLayer !== 'undefined' && state.currentStep === STEP_PRODUCT) {
            dataLayerAddToCart(order);
            dataLayerCheckout(order, 1);
            return dispatch({ type: INITIALIZE_MAIN_VIEW_FINISH })
          } else {
            return dispatch({ type: INITIALIZE_MAIN_VIEW_FINISH })
          }
        })
    } else {
      return dispatch({ type: INITIALIZE_MAIN_VIEW_FINISH })
    }
  }
}

export const UPDATE_PRODUCT_DATA = "UPDATE_PRODUCT_DATA";
export function updateProductData(data) {
  return dispatch => {
    dispatch({ type: UPDATE_PRODUCT_DATA, data })
  }
}

export const UPDATE_PRODUCT_SCHEDULE_SLOTS_DATA = "UPDATE_PRODUCT_SCHEDULE_SLOTS_DATA";
export function updateProductScheduleSlotsData(data) {
  return dispatch => {
    dispatch({ type: UPDATE_PRODUCT_SCHEDULE_SLOTS_DATA, data })
  }
}

export const ABANDON_ORDER_START = "ABANDON_ORDER_START";
export const ABANDON_ORDER_SUCCESS = "ABANDON_ORDER_SUCCESS";
export const ABANDON_ORDER_FAILURE = "ABANDON_ORDER_FAILURE";
export function abandonOrder() {
  return (dispatch, getState) => {
    const orderData = getState().orderData;

    if (!orderData.id) {
      throw new Error("abandonOrder action impossible - orderData.id is empty")
    }

    dispatch({ type: ABANDON_ORDER_START })

    return new Promise((resolve, reject) => {
      let onSuccess = data => {
        dispatch({ type: ABANDON_ORDER_SUCCESS, data })
        resolve(data)
      };

      let onFailure = error => {
        // If cancelling seems to be action_impossible, treat the order as it would have been already abandoned
        // (maybe the order had been proceeded to the next step and cannot be cancelled anymore)
        // On the backend, this will just result in creating a new order in the system.
        if (error.responseJSON && error.responseJSON.data && error.responseJSON.data.error_code === "action_impossible") {
          return onSuccess(error.responseJSON.data.order);
        }

        const errors = error.responseJSON && error.responseJSON.data && error.responseJSON.data.errors ?
          error.responseJSON.data.errors
        :
          {base: [I18n.t("base.connection_error")]};

        dispatch({ type: ABANDON_ORDER_FAILURE, error })
        dispatch({ type: SET_VALIDATION_ERRORS, errors })
        reject(error);
      };

      st.resources.orders.abandonOrder(orderData.id, orderData.token)
        .then(onSuccess, onFailure)
    });
  }
}

export const SAVE_ORDER_START = "SAVE_ORDER_START";
export const SAVE_ORDER_SUCCESS = "SAVE_ORDER_SUCCESS";
export const SAVE_ORDER_FAILURE = "SAVE_ORDER_FAILURE";
export function saveOrder(action = "SAVE") {
  return (dispatch, getState) => {
    const order = orderSelector(getState())
    const orderData = getState().orderData
    const requestBody = OrderUtils.serializeOrder(order.data)

    dispatch({ type: `${action}_ORDER_START` })

    return new Promise((resolve, reject) => {
      const onRequestSuccess = data => {
        dispatch({ type: `${action}_ORDER_SUCCESS`, data })

        // If it's calculate action, update the order data only if the orderData didnt change in the meantime
        if (action !== "CALCULATE" || getState().orderData === orderData) {
          dispatch({
            type: UPDATE_ORDER_DATA,
            data: {
              ...data.data.order,
              _participantsCustomerFieldsTouched: undefined,
            }
          })
        }

        const order = orderSelector(getState())
        if (action !== "CALCULATE") {
          dispatch(updateHistoryState())
        }

        resolve(data.data.order)
      };

      const onRequestFail = error => {
        const errors = error.responseJSON && error.responseJSON.data && error.responseJSON.data.errors ?
          error.responseJSON.data.errors
        : error && error.responseJSON && error.responseJSON.message ?
          {base: error.responseJSON.message}
        :
          {base: I18n.t("fe.new_order.errors.connection_error_during_saving")};

        dispatch({ type: `${action}_ORDER_FAILURE`, error })
        dispatch({ type: SET_VALIDATION_ERRORS, errors })
        reject(error)
      };

      const request = action === "CONFIRM" ?
        st.resources.orders.confirmOrder(requestBody)
      : action === "CALCULATE" ?
        st.resources.orders.calculateOrder(requestBody)
      : requestBody.id ?
        st.resources.orders.updateOrder(requestBody)
      :
        st.resources.orders.createOrder(requestBody);

      return request.then(onRequestSuccess, onRequestFail);
    });
  }
}

export const CONFIRM_ORDER_START = "CONFIRM_ORDER_START";
export const CONFIRM_ORDER_SUCCESS = "CONFIRM_ORDER_SUCCESS";
export const CONFIRM_ORDER_FAILURE = "CONFIRM_ORDER_FAILURE";
export function confirmOrder() {
  return saveOrder("CONFIRM");
}

export const CALCULATE_ORDER_START = "CALCULATE_ORDER_START";
export const CALCULATE_ORDER_SUCCESS = "CALCULATE_ORDER_SUCCESS";
export const CALCULATE_ORDER_FAILURE = "CALCULATE_ORDER_FAILURE";
export function calculateOrder() {
  return saveOrder("CALCULATE");
}

export function calculateOrderPricing() {
  return (dispatch, getState) => {
    return dispatch(calculateOrder())
  }
}

export const SELECT_PRODUCT_SCHEDULE_SLOT = "SELECT_PRODUCT_SCHEDULE_SLOT";
export function selectProductScheduleSlot(slot) {
  return dispatch => {
    dispatch(clearValidationErrors())
    dispatch({ type: SELECT_PRODUCT_SCHEDULE_SLOT, slot })
  }
}

export const VALIDATE_CALENDAR_FORM_CALL = "VALIDATE_CALENDAR_FORM_CALL";
export function validateCalendarForm() {
  return (dispatch, getState) => {
    dispatch({ type: VALIDATE_CALENDAR_FORM_CALL })

    return new Promise((resolve, reject) => {
      const errors = {base: []};
      const state = getState();
      const order = orderSelector(state);

      if (!order.product_variant) {
        errors.product_variant_id = [
          I18n.t("fe.new_order.errors.product_variant_not_found")
        ];
      }

      if (order.product.booking_type === "static_date_booking") {
        if (!order.product_schedule_slot || !order.product_schedule_slot.stock_available) {
          errors.base.push(
            I18n.t("fe.new_order.errors.product_schedule_slot_missing")
          );
        }
      }

      const isValid = isErrorsObjectEmpty(errors)
      resolve({isValid, errors});
    });
  };
}

export const SUBMIT_CALENDAR_FORM_CALL = "SUBMIT_CALENDAR_FORM_CALL";
export function submitCalendarForm() {
  return (dispatch, getState) => {
    dispatch({ type: SUBMIT_CALENDAR_FORM_CALL })

    return dispatch(validateCalendarForm())
      .then(({isValid, errors}) => {
        if (!isValid) {
          dispatch(setValidationErrors(errors));
          scrollToTop();
          return Promise.reject("form.invalid");
        }
      })
      .then(() => {
        const state = getState();
        const order = orderSelector(state);

        if (!state.isWidgetView) {
          if (!order.data.ordered_product_pricelist_items.length) {
            dispatch(updateOrderData({
              ordered_product_pricelist_items: [
                {
                  product_pricelist_item: order.product_variant.product_pricelist_items[0],
                  quantity: 1,
                  ordered_product_order_fields: []
                }
              ]
            }))
          }
          return dispatch(calculateOrderPricing())
        } else {
          const url = getNewOrderUrl({
            product_id: state.productData.id,
            product_variant_id: state.orderData.product_variant_id,
            product_schedule_slot_id: state.orderData.product_schedule_slot.id
          })
          window.location.href = st.urlWithCurrentQuery(url)
          return Promise.reject(`Redirecting to order website: ${url}`)
        }
      });
  };
}

export function updateOrderedProductPricelistItem(nextOrderedItem) {
  return (dispatch, getState) => {
    const order = orderSelector(getState())
    const nextOrder = OrderUtils.updateOrderedProductPricelistItem(order.data, nextOrderedItem)
    dispatch(updateOrderData(nextOrder))
  }
}

export function updateOrderedProductOrderField(parent, { product_order_field }, value) {
  return (dispatch, getState) => {
    const order = orderSelector(getState())
    const nextOrder = OrderUtils.updateOrderedProductOrderField(order.data, parent, { product_order_field }, value)
    dispatch(updateOrderData(nextOrder))
  }
}

export function updateOrderCustomerParticipant(participantPosition, participantData, touchParticipants = true) {
  return (dispatch, getState) => {
    const order = orderSelector(getState())
    const participant = order.data.order_customer_participants
      .find(participant => participant.position === participantPosition)
    const nextParticipant = {
      ...participant,
      ...participantData
    }
    const nextOrder = OrderUtils.updateOrderCustomerParticipant(order.data, nextParticipant, touchParticipants)
    return dispatch(updateOrderData(nextOrder))
  }
}

export const VALIDATE_PRODUCT_FORM_CALL = "VALIDATE_PRODUCT_FORM_CALL";
export function validateProductForm() {
  return (dispatch, getState) => {
    dispatch({ type: VALIDATE_PRODUCT_FORM_CALL })

    return new Promise((resolve, reject) => {
      let {errors} = dispatch(validateCalendarForm());
      const state = getState();
      const order = orderSelector(state);

      errors = {
        ...errors
      }

      if (!order.product_quantity) {
        // errors.product_quantity.push(
        //   I18n.t("fe.new_order.errors.missing_value")
        // );
      }

      // if (order.data.product_quantity > order.max_quantity) {
      //   errors.product_quantity.push(I18n.t("fe.new_order.errors.max_quantity_limit", {max_quantity: order.max_quantity}));
      // }

      const isValid = isErrorsObjectEmpty(errors)
      resolve({isValid, errors});
    });
  };
}

export const SUBMIT_PRODUCT_FORM_CALL = "SUBMIT_PRODUCT_FORM_CALL";
export function submitProductForm() {
  return (dispatch, getState) => {
    dispatch({ type: SUBMIT_PRODUCT_FORM_CALL })

    return dispatch(validateProductForm())
      .then(({isValid, errors}) => {
        const state = getState();

        if (!isValid) {
          dispatch(setValidationErrors(errors));
          scrollToTop();
          return Promise.reject("form.invalid");
        } else {
          return dispatch(saveOrder());
        }
      });
  };
}

export const VALIDATE_CUSTOMER_FORM_CALL = "VALIDATE_CUSTOMER_FORM_CALL";
export function validateCustomerForm() {
  return (dispatch, getState) => {
    dispatch({ type: VALIDATE_CUSTOMER_FORM_CALL })

    return new Promise((resolve, reject) => {
      let {errors} = dispatch(validateProductForm());
      const state = getState();
      const order = orderSelector(state);

      errors = {
        ...errors,
        rules_agreement: [],
        personal_data_agreement: [],
        rodo_agreement: [],
        partners_agreement: [],
      }

      if (order.requiresCustomerRulesAgreement) {
        if (!order.data.rules_agreement) {
          errors.rules_agreement.push(
            I18n.t("fe.new_order.errors.rules_agreement_missing")
          );
        }

        if (!order.data.personal_data_agreement) {
          errors.personal_data_agreement.push(
            I18n.t("fe.new_order.errors.personal_data_agreement_missing")
          );
        }

        if (!order.data.rodo_agreement) {
          errors.rodo_agreement.push(
            I18n.t("fe.new_order.errors.rodo_agreement_missing")
          );
        }

        if (!order.data.partners_agreement) {
          errors.partners_agreement.push(
            I18n.t("fe.new_order.errors.partners_agreement_missing")
          );
        }
      }

      const isValid = isErrorsObjectEmpty(errors)
      resolve({isValid, errors});
    });
  };
}

export const SUBMIT_CUSTOMER_FORM_CALL = "SUBMIT_CUSTOMER_FORM_CALL";
export function submitCustomerForm() {
  return (dispatch, getState) => {
    dispatch({ type: SUBMIT_CUSTOMER_FORM_CALL })

    return dispatch(validateCustomerForm())
      .then(({isValid, errors}) => {
        if (!isValid) {
          dispatch(setValidationErrors(errors));
          scrollToTop();
          return Promise.reject("form.invalid");
        } else {
          return dispatch(confirmOrder()).then(() => {
            const order = orderSelector(getState())
            if (!order.still_building) {
              dispatch(updateOrderData({
                _hasOrderJustFinished: true,
              }))
            }
          })
        }
      });
  };
}

export const VALIDATE_PAYMENT_FORM_CALL = "VALIDATE_PAYMENT_FORM_CALL";
export function validatePaymentForm() {
  return (dispatch, getState) => {
    dispatch({ type: VALIDATE_PAYMENT_FORM_CALL })

    return new Promise((resolve, reject) => {
      let errors = {base: []};
      const state = getState();
      const order = orderSelector(state);

      errors = {
        ...errors,
        rules_agreement: [],
        personal_data_agreement: [],
        rodo_agreement: [],
        partners_agreement: [],
      }

      if (order.requiresCustomerRulesAgreement) {
        if (!order.data.rules_agreement) {
          errors.rules_agreement.push(
            I18n.t("fe.new_order.errors.rules_agreement_missing")
          );
        }

        if (!order.data.personal_data_agreement) {
          errors.personal_data_agreement.push(
            I18n.t("fe.new_order.errors.personal_data_agreement_missing")
          );
        }

        if (!order.data.rodo_agreement) {
          errors.rodo_agreement.push(
            I18n.t("fe.new_order.errors.rodo_agreement_missing")
          );
        }

        if (!order.data.partners_agreement) {
          errors.partners_agreement.push(
            I18n.t("fe.new_order.errors.partners_agreement_missing")
          );
        }
      }

      const isValid = isErrorsObjectEmpty(errors)
      resolve({isValid, errors});
    });
  };
}

function createOrderPaymentOperation() {
  return (dispatch, getState) => {
    const { orderData, paymentFormData: { gateway } } = getState()
    return st.resources.orders.createPaymentOperation(
      orderData.id,
      orderData.token,
      {
        amount: orderData.confirmation_installment_transaction_amount,
        source_type: gateway,
      }
    )
  }
}

export const CHANGE_PAYMENT_GATEWAY = "CHANGE_PAYMENT_GATEWAY"
export function changePaymentGateway(gateway) {
  return (dispatch, getState) => {
    dispatch({ type: CHANGE_PAYMENT_GATEWAY, gateway })
    dispatch(submitPaymentForm())
  }
}

export const PAYMENT_FORM_SUBMIT_CALL = "PAYMENT_FORM_SUBMIT_CALL";
export const PAYMENT_FORM_SUBMIT_START = "PAYMENT_FORM_SUBMIT_START";
export const PAYMENT_FORM_SUBMIT_SUCCESS = "PAYMENT_FORM_SUBMIT_SUCCESS";
export const PAYMENT_FORM_SUBMIT_FAILURE = "PAYMENT_FORM_SUBMIT_FAILURE";
export const PAYMENT_FORM_UPDATE_PAYMENT_OPERATION = "PAYMENT_FORM_UPDATE_PAYMENT_OPERATION";
export function submitPaymentForm() {
  return (dispatch, getState) => {
    dispatch({ type: PAYMENT_FORM_SUBMIT_CALL })
    return dispatch(validatePaymentForm())
      .then(({isValid, errors}) => {
        if (!isValid) {
          dispatch(setValidationErrors(errors));
          scrollToTop();
          return Promise.reject("form.invalid")
        }
      })
      .then(() => {
        dispatch({ type: PAYMENT_FORM_SUBMIT_START })
        return dispatch(saveOrder())
          .then(() => {
            return dispatch(createOrderPaymentOperation())
            .then(
              data => dispatch({ type: PAYMENT_FORM_UPDATE_PAYMENT_OPERATION, payment_operation: data.payment_operation })
            )
          })
          .then(
            () => dispatch({ type: PAYMENT_FORM_SUBMIT_SUCCESS }),
            error => {
              const errors = (error && error.responseJSON && error.responseJSON.data && error.responseJSON.data.errors)
                ? error.responseJSON.data.errors
                : { base: [I18n.t("base.connection_error")] }
              dispatch({ type: PAYMENT_FORM_SUBMIT_FAILURE, error: error })
              dispatch({ type: SET_VALIDATION_ERRORS, errors })
              return Promise.reject()
            }
          )
      })
  }
}

export const SET_VALIDATION_ERRORS = "SET_VALIDATION_ERRORS";
export function setValidationErrors(errors) {
  return { type: SET_VALIDATION_ERRORS, errors };
}

export function clearValidationErrors() {
  return (dispatch, getState) => {
    const errors = getState().errors
    if (!isErrorsObjectEmpty(errors)) {
      dispatch(setValidationErrors({}))
    }
  }
}

export const APPROVE_ORDER = "APPROVE_ORDER"
export const APPROVE_ORDER_START = "APPROVE_ORDER_START"
export const APPROVE_ORDER_SUCCESS = "APPROVE_ORDER_SUCCESS"
export const APPROVE_ORDER_FAILURE = "APPROVE_ORDER_FAILURE"
export function approveOrder() {
  return (dispatch, getState) => {
    const orderData = getState().orderData;
    dispatch({ type: APPROVE_ORDER_START })
    return st.resources.orders.approveOrder(orderData.id, orderData.token)
      .then(
        data => {
          dispatch({ type: APPROVE_ORDER_SUCCESS, data })
          dispatch({ type: UPDATE_ORDER_DATA, data: data.data.order })
          return data
        },
        error => dispatch({ type: APPROVE_ORDER_FAILURE, error })
      );
  }
}

export const REJECT_ORDER = "REJECT_ORDER"
export const REJECT_ORDER_START = "REJECT_ORDER_START"
export const REJECT_ORDER_SUCCESS = "REJECT_ORDER_SUCCESS"
export const REJECT_ORDER_FAILURE = "REJECT_ORDER_FAILURE"
export function rejectOrder() {
  return (dispatch, getState) => {
    const orderData = getState().orderData;
    dispatch({ type: REJECT_ORDER_START })
    return st.resources.orders.rejectOrder(orderData.id, orderData.token)
      .then(
        data => {
          dispatch({ type: REJECT_ORDER_SUCCESS, data })
          dispatch({ type: UPDATE_ORDER_DATA, data: data.data.order })
          return data
        },
        error => dispatch({ type: REJECT_ORDER_FAILURE, error })
      );
  }
}

export const SET_IS_SMALL_VIEW = "SET_IS_SMALL_VIEW";
export function setIsWidgetView(isWidgetView) {
  return { type: SET_IS_SMALL_VIEW, data: isWidgetView }
}

export const TRIGGER_WINDOW_RESIZE = "TRIGGER_WINDOW_RESIZE"
export function triggerWindowResize(data = { width }) {
  return { type: TRIGGER_WINDOW_RESIZE, data };
}

export function dataLayerCheckout(order, step) {
  const actionField = {'step': step};
  if (step === 2) {
    const paymentOption = order.data.possible_payment_profiles.find(x => x.id === order.data.payment_profile_id);
    if (typeof paymentOption !== 'undefined') {
      actionField['option'] = paymentOption.name
    }
  }
  var currencyCode = ''
  if (typeof order.data.order_items[0] !== 'undefined') {
    currencyCode = order.data.order_items[0].total_price.currency
  }

  dataLayer.push({
    'event': 'checkout',
    'ecommerce': {
      'currencyCode': currencyCode,
      'checkout': {
        'actionField': actionField,
        'products': [
          dataLayerProductsData(order)
        ]
      }
    }
  });
}

export function dataLayerAddToCart(order) {
  var currencyCode = ''
  if (typeof order.data.order_items[0] !== 'undefined') {
    currencyCode = order.data.order_items[0].total_price.currency
  }

  dataLayer.push({
    'event': 'addToCart',
    'ecommerce': {
      'currencyCode': currencyCode,
      'add': {
        'products': [
          dataLayerProductsData(order)
        ]
      }
    }
  });
}

export function dataLayerRemoveFromCart(order) {
  dataLayer.push({
    'event': 'removeFromCart',
    'ecommerce': {
      'remove': {
        'products': [
          dataLayerProductsData(order)
        ]
      }
    }
  });
}

export function dataLayerProductsData(order) {
  let items = order.data.order_items;
  if (items !== undefined && items != null) {
    return items.map(order_item => dataLayerOrderItemData(order, order_item))
  } else {
    return Array()
  }
}

export function dataLayerOrderItemData(order, order_item) {
  return {
    'name': order_item.google_analytics_item_name,
    'id': order._product_variant.id,
    'price': order_item.unit_price.value,
    'quantity': order_item.quantity,
    'brand': order.data.company.name,
    'category': order.product.categories_and_locations_string,
    'variant': order_item.name,
    'dimension1': order.data.stock_start_date_ga_format,
    'dimension2': order_item.google_analytics_item_type,
    'metric1': order.data.stock_days_duration,
  }
}

export function scrollToTop() {
  window.scrollTo(0, 0)
}



// WEBPACK FOOTER //
// ./app/webpack/frontend/features/NewOrderView/actions/index.js